import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, ShieldCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function SchoolReviews() {
  const [items, setItems] = useState([]);
  const [replyDraft, setReplyDraft] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`${V1}/reviews/school-owner/mine`).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const reply = async (id) => {
    const text = (replyDraft[id] || "").trim();
    if (!text) return toast.error("Yanıt metni gerekli");
    setBusy(id);
    try {
      await api.patch(`${V1}/reviews/school-owner/${id}/reply`, { reply: text });
      toast.success("Yanıtınız yayınlandı");
      setReplyDraft((s) => ({ ...s, [id]: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); }
    finally { setBusy(null); }
  };

  const approved = items.filter((r) => r.approved);
  const pending = items.filter((r) => !r.approved);

  return (
    <div className="space-y-6" data-testid="school-reviews-page">
      <div>
        <div className="overline text-accent">Veli Yorumları</div>
        <h1 className="font-display text-3xl font-bold">Okulumuza Gelen Yorumlar</h1>
        <p className="text-muted-foreground mt-1">Yayınlanan yorumlara resmi yanıt verebilirsiniz. Yanıtlar okul detay sayfasında görünür.</p>
      </div>

      {pending.length > 0 && (
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-4">
          <div className="text-sm">
            <b>{pending.length}</b> yorum yönetici moderasyonunu bekliyor. Onaylandıktan sonra burada yanıtlanabilir olacak.
          </div>
        </div>
      )}

      <div className="space-y-4">
        {approved.length === 0 && <div className="p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">Henüz yayınlanmış yorum yok.</div>}
        {approved.map((r) => (
          <div key={r.id} className="p-6 rounded-xl border border-border bg-card" data-testid={`sch-review-${r.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                ))}
                {r.verified && <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-success bg-success/10 px-2 py-0.5 rounded"><ShieldCheck className="h-3 w-3" /> Doğrulanmış</span>}
              </div>
              <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("tr-TR")}</div>
            </div>
            {r.title && <div className="font-display font-bold mb-1">{r.title}</div>}
            <div className="text-sm">{r.body}</div>
            <div className="text-xs text-muted-foreground mt-2">— {r.author_name} · {r.role}</div>

            {r.admin_reply ? (
              <div className="mt-4 p-3 rounded bg-primary/5 border-l-2 border-primary">
                <div className="overline text-primary mb-1 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Okul Yanıtı</div>
                <div className="text-sm">{r.admin_reply}</div>
                <div className="mt-2">
                  <Textarea
                    rows={2}
                    placeholder="Yanıtı güncelle..."
                    value={replyDraft[r.id] || ""}
                    onChange={(e) => setReplyDraft((s) => ({ ...s, [r.id]: e.target.value }))}
                    data-testid={`reply-textarea-${r.id}`}
                  />
                  <Button size="sm" className="mt-2" onClick={() => reply(r.id)} disabled={busy === r.id} data-testid={`reply-btn-${r.id}`}>
                    Yanıtı Güncelle
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 pt-4 border-t border-border">
                <Textarea
                  rows={3}
                  placeholder="Bu yoruma resmi yanıtınızı yazın…"
                  value={replyDraft[r.id] || ""}
                  onChange={(e) => setReplyDraft((s) => ({ ...s, [r.id]: e.target.value }))}
                  data-testid={`reply-textarea-${r.id}`}
                />
                <Button size="sm" className="mt-2" onClick={() => reply(r.id)} disabled={busy === r.id} data-testid={`reply-btn-${r.id}`}>
                  <MessageSquare className="h-3 w-3" /> Yanıtla
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
